import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, Mail, Calendar, Clock, Send, Users, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { VendorInquiry, useVendorInquiries, useReplyToInquiry } from '@/hooks/useVendor';

interface VendorMessagesTabProps {
  vendorId: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendiente', color: 'bg-accent/20 text-accent' },
  replied: { label: 'Respondido', color: 'bg-secondary/20 text-secondary' },
  booked: { label: 'Reservado', color: 'bg-primary/20 text-primary' },
  declined: { label: 'Rechazado', color: 'bg-muted text-muted-foreground' },
};

const VendorMessagesTab = ({ vendorId }: VendorMessagesTabProps) => {
  const { data: inquiries = [], isLoading } = useVendorInquiries(vendorId);
  const replyMutation = useReplyToInquiry();

  const [selectedInquiry, setSelectedInquiry] = useState<VendorInquiry | null>(null);
  const [replyText, setReplyText] = useState('');
  const [newStatus, setNewStatus] = useState('replied');
  const [filter, setFilter] = useState('all');

  const filteredInquiries = inquiries.filter(inq => {
    if (filter === 'all') return true;
    return inq.status === filter;
  });

  const pendingCount = inquiries.filter(inq => inq.status === 'pending').length;

  const handleReply = () => {
    if (!selectedInquiry || !replyText.trim()) return;

    replyMutation.mutate(
      { id: selectedInquiry.id, reply: replyText, status: newStatus },
      {
        onSuccess: () => {
          setSelectedInquiry(null);
          setReplyText('');
          setNewStatus('replied');
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Mensajes</h2>
          <p className="text-muted-foreground">
            Consultas de parejas interesadas en tus servicios
          </p>
        </div>
        {pendingCount > 0 && (
          <Badge variant="default" className="gap-1">
            <MessageSquare className="h-3 w-3" />
            {pendingCount} pendiente{pendingCount > 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'replied', 'booked', 'declined'].map(status => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
          >
            {status === 'all' ? 'Todos' : STATUS_CONFIG[status]?.label || status}
            {status !== 'all' && (
              <span className="ml-1 text-xs opacity-70">
                ({inquiries.filter(i => i.status === status).length})
              </span>
            )}
          </Button>
        ))}
      </div>

      {filteredInquiries.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">Sin mensajes</h3>
            <p className="text-muted-foreground text-center">
              {filter === 'all'
                ? 'Aún no has recibido consultas de parejas'
                : `No hay mensajes con estado "${STATUS_CONFIG[filter]?.label || filter}"`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredInquiries.map(inquiry => {
            const statusConfig = STATUS_CONFIG[inquiry.status] || STATUS_CONFIG.pending;

            return (
              <Card
                key={inquiry.id}
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setSelectedInquiry(inquiry)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{inquiry.couple_name}</h4>
                          <Badge className={statusConfig.color} variant="outline">
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {inquiry.message}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {inquiry.couple_email}
                          </span>
                          {inquiry.wedding_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Boda: {format(new Date(inquiry.wedding_date), 'd MMM yyyy', { locale: es })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-xs text-muted-foreground shrink-0">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {format(new Date(inquiry.created_at), 'd MMM', { locale: es })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Reply Sheet */}
      <Sheet open={!!selectedInquiry} onOpenChange={(open) => !open && setSelectedInquiry(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {selectedInquiry && (
            <>
              <SheetHeader>
                <SheetTitle className="font-serif">{selectedInquiry.couple_name}</SheetTitle>
                <SheetDescription>{selectedInquiry.couple_email}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Original Message */}
                <div>
                  <h4 className="text-sm font-medium mb-2">Mensaje Original</h4>
                  <Card className="bg-muted/50">
                    <CardContent className="p-4">
                      <p className="text-sm whitespace-pre-wrap">{selectedInquiry.message}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span>
                          Recibido: {format(new Date(selectedInquiry.created_at), "d MMMM yyyy, HH:mm", { locale: es })}
                        </span>
                        {selectedInquiry.wedding_date && (
                          <span>
                            Fecha boda: {format(new Date(selectedInquiry.wedding_date), 'd MMMM yyyy', { locale: es })}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Previous Reply */}
                {selectedInquiry.vendor_reply && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-secondary" />
                      Tu Respuesta
                    </h4>
                    <Card className="bg-secondary/10 border-secondary/20">
                      <CardContent className="p-4">
                        <p className="text-sm whitespace-pre-wrap">{selectedInquiry.vendor_reply}</p>
                        {selectedInquiry.replied_at && (
                          <p className="text-xs text-muted-foreground mt-3">
                            Enviado: {format(new Date(selectedInquiry.replied_at), "d MMMM yyyy, HH:mm", { locale: es })}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Reply Form */}
                <div>
                  <h4 className="text-sm font-medium mb-2">
                    {selectedInquiry.vendor_reply ? 'Actualizar Respuesta' : 'Responder'}
                  </h4>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Escribe tu respuesta..."
                    rows={5}
                    className="mb-3"
                  />
                  <div className="flex items-center gap-3">
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="replied">Respondido</SelectItem>
                        <SelectItem value="booked">Reservado</SelectItem>
                        <SelectItem value="declined">Rechazado</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleReply}
                      disabled={!replyText.trim() || replyMutation.isPending}
                      className="flex-1 gap-2"
                    >
                      <Send className="h-4 w-4" />
                      {replyMutation.isPending ? 'Enviando...' : 'Enviar Respuesta'}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default VendorMessagesTab;
